// UbicacionPicker.jsx — Picker granular de ubicación (país/provincia/ciudad/calle/número)
// Para Argentina usa la API pública GeoRef (apis.datos.gob.ar/georef) — sin API key, datos oficiales.
// Cachea respuestas en sessionStorage para no llamar varias veces en la misma sesión.

import React, { useEffect, useState } from "react";

const GEOREF_BASE = "https://apis.datos.gob.ar/georef/api";
const PAISES = ["Argentina", "Chile", "Uruguay", "Brasil", "Paraguay", "Bolivia", "Otro"];

// Helper de fetch con cache en sessionStorage
const fetchCached = async (key, url) => {
  const cached = sessionStorage.getItem(key);
  if (cached) {
    try { return JSON.parse(cached); } catch {}
  }
  try {
    const r = await fetch(url);
    const data = await r.json();
    sessionStorage.setItem(key, JSON.stringify(data));
    return data;
  } catch {
    return null;
  }
};

const S = {
  section: { display: "flex", flexDirection: "column", gap: 8 },
  row:     { display: "grid", gap: 8 },
  label:   { fontSize: 12, fontWeight: 600, color: "#444", marginBottom: 2 },
  input:   { width: "100%", padding: "8px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 14, boxSizing: "border-box" },
  hint:    { fontSize: 11, color: "#888", marginTop: 2 },
};

const UbicacionPicker = ({ value = {}, onChange, disabled = false }) => {
  const { pais = "Argentina", provincia = "", ciudad = "", calle = "", numero = "" } = value || {};

  const [provincias, setProvincias]   = useState([]);  // [{nombre}]
  const [localidades, setLocalidades] = useState([]);  // [{nombre}]
  const [loadingProv, setLoadingProv] = useState(false);
  const [loadingLoc,  setLoadingLoc]  = useState(false);
  const [apiError,    setApiError]    = useState(false);

  const esArgentina = pais === "Argentina";

  // Cargar provincias cuando el país es Argentina
  useEffect(() => {
    if (!esArgentina) { setProvincias([]); return; }
    setLoadingProv(true);
    fetchCached(
      "georef_provincias_ar",
      `${GEOREF_BASE}/provincias?max=30&campos=nombre&orden=nombre`
    ).then((data) => {
      if (data?.provincias) {
        setProvincias(data.provincias);
        setApiError(false);
      } else {
        setApiError(true);
      }
    }).finally(() => setLoadingProv(false));
  }, [esArgentina]);

  // Cargar localidades cuando hay provincia
  useEffect(() => {
    if (!esArgentina || !provincia) { setLocalidades([]); return; }
    setLoadingLoc(true);
    const key = `georef_localidades_${provincia}`;
    fetchCached(
      key,
      `${GEOREF_BASE}/localidades?provincia=${encodeURIComponent(provincia)}&max=5000&campos=nombre&orden=nombre`
    ).then((data) => {
      if (data?.localidades) {
        // Quitar duplicados por nombre (la API a veces repite)
        const vistos = new Set();
        const unicas = data.localidades.filter((l) => {
          if (vistos.has(l.nombre)) return false;
          vistos.add(l.nombre);
          return true;
        });
        setLocalidades(unicas);
      }
    }).finally(() => setLoadingLoc(false));
  }, [esArgentina, provincia]);

  // Helper para actualizar un campo del value
  const update = (campo, valor) => {
    const next = { ...value, [campo]: valor };
    // Si cambia el país, limpiar provincia y ciudad
    if (campo === "pais") { next.provincia = ""; next.ciudad = ""; }
    // Si cambia la provincia, limpiar ciudad
    if (campo === "provincia") { next.ciudad = ""; }
    onChange(next);
  };

  return (
    <div style={S.section}>
      {/* Fila 1: País / Provincia / Ciudad */}
      <div style={{ ...S.row, gridTemplateColumns: "1fr 1fr 1fr" }}>
        <div>
          <div style={S.label}>País</div>
          <select style={S.input} value={pais} disabled={disabled}
            onChange={(e) => update("pais", e.target.value)}>
            {PAISES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div>
          <div style={S.label}>Provincia</div>
          {esArgentina && !apiError ? (
            <select style={S.input} value={provincia} disabled={disabled || loadingProv}
              onChange={(e) => update("provincia", e.target.value)}>
              <option value="">{loadingProv ? "Cargando…" : "Elegir provincia"}</option>
              {provincias.map((p) => <option key={p.nombre} value={p.nombre}>{p.nombre}</option>)}
            </select>
          ) : (
            <input style={S.input} type="text" value={provincia} disabled={disabled}
              placeholder="Provincia / región"
              onChange={(e) => update("provincia", e.target.value)} />
          )}
        </div>

        <div>
          <div style={S.label}>Ciudad / Localidad</div>
          {esArgentina && provincia && localidades.length > 0 ? (
            <input style={S.input} type="text" list="ubicacion-localidades-list"
              value={ciudad} disabled={disabled || loadingLoc}
              placeholder={loadingLoc ? "Cargando…" : "Empezá a escribir…"}
              onChange={(e) => update("ciudad", e.target.value)} />
          ) : (
            <input style={S.input} type="text" value={ciudad} disabled={disabled}
              placeholder="Ciudad / localidad"
              onChange={(e) => update("ciudad", e.target.value)} />
          )}
          {esArgentina && provincia && localidades.length > 0 && (
            <datalist id="ubicacion-localidades-list">
              {localidades.map((l) => <option key={l.nombre} value={l.nombre} />)}
            </datalist>
          )}
        </div>
      </div>

      {/* Fila 2: Calle / Número */}
      <div style={{ ...S.row, gridTemplateColumns: "3fr 1fr" }}>
        <div>
          <div style={S.label}>Calle</div>
          <input style={S.input} type="text" value={calle} disabled={disabled}
            placeholder="Av. Corrientes"
            onChange={(e) => update("calle", e.target.value)} />
        </div>
        <div>
          <div style={S.label}>Número</div>
          <input style={S.input} type="text" value={numero} disabled={disabled}
            placeholder="1234"
            onChange={(e) => update("numero", e.target.value)} />
        </div>
      </div>

      {apiError && esArgentina && (
        <div style={S.hint}>⚠️ No se pudo cargar la lista oficial. Podés escribir provincia y ciudad a mano.</div>
      )}
    </div>
  );
};

export default UbicacionPicker;
